<template>
    <div class="data-container">
        <el-tabs class="elTabs" v-model="tab_active" @tab-change="tabChange">
            <el-tab-pane label="温采传感器" name="1"></el-tab-pane>
            <el-tab-pane label="管道传感器" name="2"></el-tab-pane>
        </el-tabs>
        <div class="tool-box">
            <el-button type="primary" :icon="Refresh" @click="getTableData">刷新</el-button>
            <el-button type="primary" :icon="Plus" plain @click="onAddClick()" v-permission="['heating:temp-calibration:add']">
                添加
            </el-button>
            <el-button
                type="primary"
                :icon="Download"
                plain
                @click="uploadVisible = true"
                v-permission="['heating:temp-calibration:export']"
            >
                导入
            </el-button>
            <el-button
                type="primary"
                plain
                @click="setNormalTemp(selected_ids, 1, 20)"
                v-permission="['heating:temp-calibration:set-normal-temp']"
            >
                批量设置标准温度
            </el-button>
            <el-button
                type="primary"
                :icon="Upload"
                plain
                @click="onRemoveClick(selected_ids)"
                v-permission="['heating:temp-calibration:remove']"
            >
                批量删除
            </el-button>
        </div>
        <el-form class="search-form" ref="search_form" :inline="true" :model="search_params" label-position="top">
            <el-form-item label="IMEI" prop="IMEI">
                <el-input style="width: 165px" placeholder="请输入IMEI" v-model="search_params.IMEI" clearable />
            </el-form-item>
            <el-form-item label="状态" prop="online">
                <el-select v-model="search_params.online" placeholder="请选择状态" style="width: 165px" clearable>
                    <el-option
                        v-for="item in statusList"
                        :key="item.value"
                        :label="item.name"
                        :value="Number(item.value)"
                    ></el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="ICCID" prop="ICCID">
                <el-input style="width: 165px" placeholder="请输入ICCID" v-model="search_params.ICCID" clearable />
            </el-form-item>
            <el-form-item>
                <el-button type="primary" :icon="Search" @click="getTableData">搜索</el-button>
                <el-button :icon="Refresh" @click="resetQueryForm">重置</el-button>
            </el-form-item>
        </el-form>
        <el-table
            :data="table_data"
            border
            v-loading="tableLoading"
            row-key="id"
            :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
            max-height="calc(100vh - 360px)"
            @selection-change="handleSelectionChange"
        >
            <el-table-column type="selection" width="55" />
            <el-table-column label="IMEI" min-width="100" prop="IMEI"></el-table-column>
            <el-table-column label="设备类型型号" min-width="150" prop="device_type">
                <template #default="{ row }">
                    <span>{{ row.model ? model_list[row.model].type + '/' + model_list[row.model].model : '' }}</span>
                </template>
            </el-table-column>
            <el-table-column label="版本号" width="100px" prop="version"></el-table-column>
            <el-table-column label="ICCID" min-width="150" prop="ICCID"></el-table-column>
            <el-table-column label="状态" width="90" prop="online" align="center">
                <template #default="{ row }">
                    <span v-if="row.online" style="color: #67c23a">在线</span>
                    <span v-else style="color: #f56c6c">离线</span>
                </template>
            </el-table-column>
            <el-table-column label="首次注册时间" min-width="150" prop="reg_ts"></el-table-column>
            <el-table-column label="测量读数（℃）" prop="standard_temp">
                <template #default="{ row }">
                    <span>{{ (row.standard_temp - row.calibration).toFixed(2) }}</span>
                </template>
            </el-table-column>
            <el-table-column
                label="标准温度（℃）"
                prop="standard_temp"
                :formatter="(row) => row.standard_temp.toFixed(2)"
            ></el-table-column>
            <el-table-column label="补偿温度（℃）" prop="calibration">
                <template #default="{ row }">
                    <span class="temp" :class="row.calibration > 0 ? 'up' : 'down'" v-if="row.calibration !== 0">
                        {{ row.calibration < 0 ? '↓' : '' }}
                        {{ row.calibration > 0 ? '↑' : '' }}
                        Δ{{ Math.abs(row.calibration).toFixed(2) }}
                    </span>
                    <span class="temp" v-else>0</span>
                </template>
            </el-table-column>
            <el-table-column label="操作" fixed="right" width="200">
                <template #default="{ row }">
                    <el-button
                        type="primary"
                        :icon="Setting"
                        link
                        size="small"
                        @click="setNormalTemp([row.id], row.purpose, row.standard_temp)"
                        v-permission="['heating:temp-calibration:set-normal-temp']"
                    >
                        设置标准温度
                    </el-button>
                    <el-button
                        type="primary"
                        :icon="Delete"
                        link
                        size="small"
                        @click="onRemoveClick([row.id])"
                        v-permission="['heating:temp-calibration:remove']"
                    >
                        删除
                    </el-button>
                </template>
            </el-table-column>
        </el-table>
        <Pagination
            v-if="total > 0"
            :current-page="page_params.page"
            :page-size="page_params.page_size"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
        />
        <imports-form
            v-if="uploadVisible"
            v-model="uploadVisible"
            @updateForm="getTableData"
            menuCode="heating/device"
            :params-data="{ type: tab_active }"
        />
    </div>
</template>

<script setup>
import { h, onMounted, reactive, ref } from 'vue'
import { Delete, Download, Plus, Refresh, Search, Setting, Upload } from '@element-plus/icons'
import { addIMEI, batchDeleteDevice, getDeviceList } from '@/api/heating/devicePoint/devicePoint.js'
import Pagination from '@/components/Pagination/index.vue'
import { getEquipModelTree } from '@/api/equipment.js'
import { ElInputNumber, ElMessage, ElMessageBox, ElRadio, ElRadioGroup } from 'element-plus'
import ImportsForm from '@/components/ImportsForm.vue'
import { batchSetNormalTemp } from '@/api/heating/temp-calibration.js'

// 标签页
const tab_active = ref('1')
function tabChange(tab) {
    tab_active.value = tab
    getTableData()
}

// 型号列表
const model_list = ref({})

async function getModelList() {
    await getEquipModelTree().then((data) => {
        const list = {}
        data.forEach((device_type) => {
            device_type?.children &&
                device_type.children.forEach((device_model) => {
                    list[device_model.id] = {
                        type: device_type.name,
                        model: device_model.name
                    }
                })
        })
        model_list.value = list
    })
}

// 状态列表
const statusList = [
    { name: '在线', value: 1 },
    { name: '离线', value: 0 }
]

// 重置
const search_form = ref(null)
const resetQueryForm = () => {
    search_form.value.resetFields()
    getTableData()
}
// 数据源
const search_params = reactive({
    name: '',
    online: null,
    IMEI: '',
    ICCID: ''
})

const table_data = ref([])
const tableLoading = ref(true)
const total = ref(0)

// 获取列表数据
async function getTableData() {
    tableLoading.value = true
    const params = { ...search_params, ...page_params }
    params.type = tab_active.value
    getDeviceList(params).then((data) => {
        table_data.value = data.lists
        total.value = data.total
        tableLoading.value = false
    })
}

// 分页相关
const page_params = reactive({
    page: 1,
    page_size: 10
})
const handleSizeChange = (currentSize) => {
    page_params.page_size = currentSize
    page_params.page = 1
    getTableData()
}
const handleCurrentChange = (currentPage) => {
    page_params.page = currentPage
    getTableData()
}

// 添加
const onAddClick = () => {
    ElMessageBox.prompt('请输入设备IMEI', {
        inputPattern: /^\d{15}$/,
        inputErrorMessage: '请输入15位数字',
        beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
                if (instance.inputValue) {
                    instance.confirmButtonLoading = true
                    addIMEI({
                        type: tab_active.value,
                        IMEI: instance.inputValue
                    })
                        .then(() => {
                            instance.confirmButtonLoading = false
                            ElMessage.success('添加成功')
                            getTableData()
                            done()
                        })
                        .catch(() => {
                            instance.confirmButtonLoading = false
                        })
                } else {
                    ElMessage.error('请输入设备IMEI')
                    instance.confirmButtonLoading = false
                }
            } else {
                done()
            }
        }
    })
}

// 表格项被勾选
const selected_ids = ref([])
function handleSelectionChange(list) {
    selected_ids.value = list.map((item) => {
        return item.id
    })
}

// 导入
const uploadVisible = ref(false)

// 设置标准温度
function setNormalTemp(ids, tempType = 1, tempValue = 20) {
    const temp_type = ref(tempType)
    const temp_value = ref(tempValue)
    ElMessageBox({
        title: '设置标准温度',
        message: () => {
            return h('div', {}, [
                h(
                    ElRadioGroup,
                    {
                        modelValue: temp_type.value,
                        'onUpdate:modelValue': (val) => {
                            temp_type.value = val
                        }
                    },
                    [
                        h(
                            ElRadio,
                            { label: 1 },
                            {
                                default: () => '制热'
                            }
                        ),
                        h(
                            ElRadio,
                            { label: 2 },
                            {
                                default: () => '制冷'
                            }
                        )
                    ]
                ),
                h(
                    'div',
                    {
                        style: {
                            marginTop: '10px'
                        }
                    },
                    [
                        h(ElInputNumber, {
                            modelValue: temp_value.value,
                            min: -100,
                            max: 100,
                            'onUpdate:modelValue': (val) => {
                                temp_value.value = val
                            }
                        }),
                        h('span', ' ℃')
                    ]
                )
            ])
        },
        beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
                if (temp_value.value !== null) {
                    instance.confirmButtonLoading = true
                    batchSetNormalTemp({
                        id_list: ids,
                        calibration: temp_value.value,
                        purpose: temp_type.value
                    })
                        .then(() => {
                            instance.confirmButtonLoading = false
                            ElMessage.success('设置成功！')
                            getTableData()
                            done()
                        })
                        .catch(() => {
                            instance.confirmButtonLoading = false
                        })
                } else {
                    ElMessage.error('请输入温度值')
                    instance.confirmButtonLoading = false
                }
            } else {
                done()
            }
        }
    })
}

// 删除
function onRemoveClick(ids) {
    ElMessageBox.confirm('是否确认删除选中的设备？', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(() => {
        batchDeleteDevice({
            id_list: ids
        }).then(() => {
            ElMessage.success('删除成功')
            getTableData()
        })
    })
}

onMounted(() => {
    getModelList()
    getTableData()
})
</script>

<style lang="scss" scoped>
.page-container {
    display: block;
    padding: 0 20px;
}

.temp {
    display: flex;
    align-items: center;
    vertical-align: middle;
}
.temp.up {
    color: #f56c6c;
}
.temp.down {
    color: #67c23a;
}
</style>
